import {
    ChangeDetectionStrategy,
    Component,
    effect,
    HostListener,
    inject,
    input,
    OnDestroy,
    OnInit,
    signal,
    untracked,
    viewChild
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { DisplayGrid, GridsterConfig, GridsterItemConfig, GridType } from 'angular-gridster2';
import { Subscription } from 'rxjs';

import { speedDialButtons } from '../components/layouts/speed-dial-fab/speed-dial-fab.component';
import { ActionButtonModel } from '../models/core/action-button.model';
import { DashboardCardConfigModel } from '../models/dashboard-card-config.model';
import { DashboardCardModel } from '../models/dashboard-card.model';
import { DashboardComponentTypeEnum } from '../models/enums/dashboard-component.enum';
import { DashboardEnum } from '../models/enums/dashboard.enum';
import { LayoutKeyEnum } from '../models/enums/layout-key.enum';
import { RolesEnum } from '../models/enums/roles.enum';
import { AuthService } from '../services/auth.service';
import { BasicDataService } from '../services/basic-data.service';
import { DashboardService } from '../services/dashboard.service';
import { LayoutSettingsService } from '../services/layout-settings.service';
import { UserSessionService } from '../services/user-session.service';

@Component({
    selector: 'sl-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
    readonly newWidgetDdCtxTrigger = viewChild.required<MatMenuTrigger>('newWidgetDdCtxTrigger');

    /**
     * if set to a number, we use this component as editor of the role-specific default layout
     */
    readonly editLayoutDefaultSettingsRoleId = input<number>();
    readonly layoutKey = input<LayoutKeyEnum>(LayoutKeyEnum.Default);
    readonly fixedConfig = input<DashboardCardConfigModel[]>();

    dashboardType?: DashboardEnum;
    allowedWidgetTypesForDashboard: DashboardCardModel[] = [];
    allowedWidgetsToAddAndRemove: DashboardComponentTypeEnum[] = [];
    gridsterOptions: GridsterConfig;
    ctxMenuTopPosition = '';
    ctxMenuLeftPosition = '';
    readonly cards = signal<DashboardCardModel[]>([]);
    readonly isDashboardEditable = signal(false);
    readonly isNetvote = signal(false);

    private currentGridPosition?: GridsterItemConfig;
    private cardsSubscription?: Subscription;
    private readonly currentUserRoleId = signal(0);
    private readonly dashboardService = inject(DashboardService);
    private readonly authService = inject(AuthService);
    private readonly userSessionService = inject(UserSessionService);
    private readonly basicDataService = inject(BasicDataService);
    private readonly layoutSettingsService = inject(LayoutSettingsService);

    constructor() {
        this.layoutSettingsService.onSidebarCollapsedChange.subscribe(() => {
            // trigger grister size calculation
            this.gridsterOptions = { ...this.gridsterOptions };
        });

        effect(() => {
            if (!!this.editLayoutDefaultSettingsRoleId()) {
                // to immediately make dashboard emtpy until we have loaded new content
                untracked(() => this.dashboardService.clearCards());
            }
        });
    }

    @HostListener('click')
    mouseClick(): void {
        if (this.isNetvote() || !!this.fixedConfig()) {
            return;
        }

        this.resetContextMenu();
    }

    ngOnInit(): void {
        this.isNetvote.set(this.userSessionService.hasNetvoteRole);
        this.currentUserRoleId.set(this.userSessionService.roleId);

        this.gridsterOptions = {
            ...this.dashboardService.gridsterOptions,
            emptyCellContextMenuCallback: this.contextClick.bind(this)
        };

        // since fixed configs cannot be changed, it should always fit to window
        if (!!this.fixedConfig()) {
            this.gridsterOptions.gridType = GridType.Fit;
        }

        // eslint-disable-next-line eqeqeq
        if (this.editLayoutDefaultSettingsRoleId() == RolesEnum.Netvote || this.isNetvote()) {
            this.dashboardType = DashboardEnum.Netvote;
        } else if (this.layoutKey() === LayoutKeyEnum.GeneralMeeting) {
            this.dashboardType = DashboardEnum.GeneralMeeting;
        } else {
            this.dashboardType = DashboardEnum.Sharelog;
        }

        this.setupButtons();

        this.allowedWidgetTypesForDashboard = Object.entries(this.dashboardService.componentsList)
            .map(entry => entry as [string, DashboardCardModel])
            .filter(([, widget]) => (widget.dashboards & this.dashboardType!) === this.dashboardType)
            .map(([, widget]) => widget);
        this.allowedWidgetsToAddAndRemove = this.allowedWidgetTypesForDashboard
            .filter(widget => {
                // minimum role of operator is needed to add or remove a markdown widget
                if (widget.type === DashboardComponentTypeEnum.Markdown && this.currentUserRoleId() < RolesEnum.Operator) {
                    return false;
                }

                return this.authService.hasPermission(widget.commandName);
            })
            .map(widget => widget.type);
        this.cardsSubscription = this.dashboardService.cards.subscribe(cards => {
            const allowedCards: DashboardCardModel[] = [];
            const allowedWidgetTypes = this.allowedWidgetTypesForDashboard.map(widget => widget.type);

            for (const card of cards) {
                if (allowedWidgetTypes.includes(card.type)) {
                    allowedCards.push(card);
                } else {
                    // remove this card, since it's not allowed on this dashboard
                    // but do not save the dashboard, because netvote users don't have the right to do that
                    // to save, a user with higher rights has to press edit on the dashboard and then save
                    this.dashboardService.removeCard(card);
                }
            }

            this.cards.set(allowedCards);
        });

        this.basicDataService.get().subscribe(resp => {
            if (!!resp && resp.netvoteBackgroundUrl) {
                document.documentElement.style.setProperty('--background-image', 'url("' + resp.netvoteBackgroundUrl + '")');
            }
        });

        this.dashboardService.init(this.editLayoutDefaultSettingsRoleId(), this.layoutKey(), this.fixedConfig());
    }

    ngOnDestroy(): void {
        if (!!this.cardsSubscription) {
            this.cardsSubscription.unsubscribe();
        }
    }

    addWidget(widget: DashboardComponentTypeEnum): void {
        this.dashboardService.addCardByType(widget, this.currentGridPosition);
        this.resetContextMenu();

        if (!this.isDashboardEditable()) {
            this.dashboardService.saveCurrent();
        }
    }

    removeWidget(card: DashboardCardModel): void {
        setTimeout(() => this.dashboardService.removeCard(card), 0);
    }

    contextClick(event: MouseEvent, item: GridsterItemConfig): void {
        if (this.isNetvote() || !!this.fixedConfig()) {
            return;
        }

        this.ctxMenuTopPosition = `${event.clientY}px`;
        this.ctxMenuLeftPosition = `${event.clientX}px`;
        this.currentGridPosition = item;

        this.newWidgetDdCtxTrigger().openMenu();
    }

    toggleDashboardEdit(): void {
        const editable = {
            displayGrid: DisplayGrid.Always,
            draggable: {
                enabled: true
            },
            resizable: {
                enabled: true
            }
        };
        const notEditable = {
            displayGrid: DisplayGrid.None,
            draggable: {
                enabled: false
            },
            resizable: {
                enabled: false
            }
        };

        this.isDashboardEditable.set(!this.isDashboardEditable());
        this.dashboardService.updateEditMode(this.isDashboardEditable());

        this.setupButtons();

        this.gridsterOptions = {
            ...this.gridsterOptions,
            ...(this.isDashboardEditable() ? editable : notEditable)
        };

        this.updateGridOptions();

        if (!this.isDashboardEditable()) {
            this.dashboardService.saveCurrent();
        }
    }

    onReset(): void {
        this.dashboardService.resetCurrent();
    }

    private setupButtons(): void {
        if (this.isNetvote() || !!this.fixedConfig()) {
            return;
        }

        const buttons = [
            new ActionButtonModel({
                icon: 'add',
                clickFunction: (event: MouseEvent) => {
                    this.ctxMenuTopPosition = `${event.clientY}px`;
                    this.ctxMenuLeftPosition = `${event.clientX}px`;

                    this.newWidgetDdCtxTrigger().openMenu();
                },
                color: 'primary',
                caption: 'Dashboard.AddWidget'
            }),
            new ActionButtonModel({
                icon: this.isDashboardEditable() ? 'check' : 'edit',
                clickFunction: () => this.toggleDashboardEdit(),
                color: 'accent',
                caption: this.isDashboardEditable() ? 'General.Save' : 'General.Edit'
            })
        ];

        speedDialButtons.set(buttons);
    }

    private resetContextMenu(): void {
        this.newWidgetDdCtxTrigger().closeMenu();
        this.currentGridPosition = undefined;
    }

    private updateGridOptions(): void {
        if (!!this.gridsterOptions['api']?.optionsChanged) {
            this.gridsterOptions['api'].optionsChanged();
        }
    }
}
